import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { initialPath } from "~misc/paths";
import ReportFilterBar from "../../components/ReportFilterBar";
import ReportListings from "../../components/ReportListings";
import { reportListingTypes } from "../../components/reportListingTypes";
import SelfServicesChart from "../../components/SelfServicesChart";
import StatsCard from "../../components/StatsCard";
import { montarDadosGrafico, useSelfServicesData } from "../../hooks/useSelfServicesData";


const order_options = [
  { value: "alfabetica",      label: "Ordem Alfabética" },
  { value: "maior-media",     label: "Maior Média 3M"   },
  { value: "maior-mes-atual", label: "Maior Mês Atual"  },
];

const getName = (str) => str?.split(" - ").slice(1).join(" - ") ?? str ?? "";

const getSortName = (str) => getName(str).replace(/^[\s\d.\-/]+/, "").trim() || getName(str);

const normalize = (str) => (str ?? "").toLowerCase().replace(/\s+/g, " ").trim();


const SelfServicesPage = () => {
  const { statsSections, clientes, todosClientes, cardRota, cardTodos, listingData, listingDataTodos, updatedAt, isLoading, isRefreshing } = useSelfServicesData();

  const [searchValue, setSearchValue] = useState("");
  const [orderValue, setOrderValue] = useState("alfabetica");

  const { aba } = useParams();
  const navigate = useNavigate();
  const activeActionKey = aba === "todos" ? "todos" : "rota";

  const setActiveActionKey = useCallback(
    (key, { replace = false } = {}) => {
      const base = `${initialPath}/gestao-de-vendas/autosservico`;
      navigate(key === "todos" ? `${base}/todos` : base, { replace });
    },
    [navigate]
  );

  const autoSwitchedRef = useRef(false);

  const isRotaEmpty  = (cardRota?.totalPedidos ?? 0) === 0;
  const isTodosEmpty = (cardTodos?.totalPedidos ?? 0) === 0;
  const hasTodosRows = !isTodosEmpty;
  const isSemDadosGrafico = activeActionKey === "rota" ? isRotaEmpty : isTodosEmpty;

  useEffect(() => {
    if (autoSwitchedRef.current) return;
    if (activeActionKey === "rota" && isRotaEmpty && hasTodosRows) {
      autoSwitchedRef.current = true;
      setActiveActionKey("todos", { replace: true });
    }
  }, [activeActionKey, isRotaEmpty, hasTodosRows, setActiveActionKey]);

  const showRotaEmptyNotice =
    activeActionKey === "rota" && isRotaEmpty && hasTodosRows;

  const sourceListingData = activeActionKey === "todos" ? listingDataTodos : listingData;

  const q = normalize(searchValue);

  const sourceClientes = activeActionKey === "todos" ? todosClientes : clientes;

  const filteredClientes = useMemo(() => {
    if (!q) return sourceClientes;
    return sourceClientes.filter(
      (c) => normalize(`${c.codigo} - ${c.razaoSocial}`).includes(q),
    );
  }, [sourceClientes, q]);

  const chartData = useMemo(() => montarDadosGrafico(filteredClientes), [filteredClientes]);

  const filteredListingData = useMemo(() => {
    const source = sourceListingData?.autosservicos;
    if (!source) return sourceListingData;

    let rows = q
      ? source.rows.filter((r) => normalize(r.cliente).includes(q))
      : source.rows;

    if (orderValue === "maior-media") {
      rows = [...rows].sort((a, b) => Number(b.media3mRaw) - Number(a.media3mRaw));
    } else if (orderValue === "maior-mes-atual") {
      rows = [...rows].sort((a, b) => Number(b.pedidosMesAtual) - Number(a.pedidosMesAtual));
    } else {
      rows = [...rows].sort((a, b) =>
        getSortName(a.cliente).localeCompare(getSortName(b.cliente), "pt-BR", { sensitivity: "base" }),
      );
    }

    return { autosservicos: { ...source, rows } };
  }, [sourceListingData, q, orderValue]);

  if (isLoading && !statsSections?.length) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className="text-[#1A2B49] font-semibold">Carregando...</span>
      </div>
    );
  }

  return (
    <div className="pb-10">
      {isRefreshing && (
        <div className="container">
          <div className="w-[130px] flex mt-4 items-center gap-2 bg-[#01054C] rounded-md px-4 py-2 text-xs font-semibold text-[#fff] animate-pulse">
            <span className="rounded-full" />
            Atualizando...
          </div>
        </div>
      )}

      <StatsCard
        sections={statsSections}
        sectionId="autosservico"
        activeActionKey={activeActionKey}
        onActionChange={(_sectionId, actionKey) => {
          setActiveActionKey(actionKey);
          setSearchValue("");
        }}
      />

      {showRotaEmptyNotice && (
        <div className="container">
          <div className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-[#1A2B49] md:flex-row md:items-center md:justify-between">
            <span>Não há dados para os clientes da rota de hoje.</span>
            <button
              type="button"
              onClick={() => setActiveActionKey("todos")}
              className="self-start rounded-md border border-[#0655A3] bg-white px-3 py-1.5 text-xs font-semibold text-[#0655A3] hover:bg-[#0655A3] hover:text-white md:self-auto"
            >
              Ver todos os clientes
            </button>
          </div>
        </div>
      )}

      <div className="container py-2 mb-2">
        <ReportFilterBar
          searchValue={searchValue}
          searchPlaceholder="Busque por Código de Cliente ou Razão Social"
          onSearchValueChange={setSearchValue}
          onSearchSubmit={setSearchValue}
          showClearButton={!!searchValue}
          onClearFilters={() => setSearchValue("")}
          showOrder={false}
        />
      </div>

      <SelfServicesChart
        chartData={chartData}
        emptyMessage={isSemDadosGrafico ? "Não há clientes roteirizados hoje" : undefined}
      />

      <div className="container flex justify-end items-center gap-2 py-4">
        <span className="text-sm text-[#1A2B49]">Ordenar por:</span>
        <div className="relative">
          <select
            value={orderValue}
            onChange={(e) => setOrderValue(e.target.value)}
            className="appearance-none rounded-md bg-transparent pl-1 pr-11 text-[17px] font-bold text-[#050B5C] outline-none"
          >
            {order_options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2">
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 1.125L4 3.875L1 1.125" stroke="#01054C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>

      <ReportListings
        type={reportListingTypes.autosservicos}
        dataByType={filteredListingData}
        updatedAt={updatedAt}
        footerNote="Atualização uma vez ao dia com referência d-1"
      />
    </div>
  );
};

export default SelfServicesPage;
